export const SORT_PANEL_STORAGE_PREFIX = 'sort-panel::';

export type StoredSortPanelState = {
  version: 2;
  savedAtIso: string;
  valuesByFieldKey: {
    wasCleared: boolean; // true -> explicitly cleared | false -> expect sortKey + sortDirection
    sortKey: string | null; // UI sort key (Column.field)
    sortDirection: 'ASC' | 'DESC' | null;
  };
};

export function getSortPanelStorageKey(tableName: string): string | null {
  const normalizedTableName = String(tableName ?? '').trim();

  if (normalizedTableName.length === 0) {
    return null;
  }

  return `${SORT_PANEL_STORAGE_PREFIX}${normalizedTableName}`;
}

export function loadStoredSortPanelState(tableName: string): StoredSortPanelState | null {
  const storageKey = getSortPanelStorageKey(tableName);

  if (!storageKey) {
    return null;
  }

  const raw = localStorage.getItem(storageKey);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredSortPanelState;

    if (!isValidStoredSortPanelState(parsed)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function persistSortPanelState(params: { tableName: string; sortKey: string | null; sortDirection: 'ASC' | 'DESC' | null; wasCleared: boolean; }): StoredSortPanelState | null {
  const storageKey = getSortPanelStorageKey(params.tableName);

  if (!storageKey) {
    return null;
  }

  const normalizedSortKey = normalizeNullableString(params.sortKey);

  let normalizedDirection: 'ASC' | 'DESC' | null = null;
  if (params.sortDirection === 'ASC' || params.sortDirection === 'DESC') {
    normalizedDirection = params.sortDirection;
  }

  const normalizedWasCleared = params.wasCleared === true;

  const state: StoredSortPanelState = {
    version: 2,
    savedAtIso: new Date().toISOString(),
    valuesByFieldKey: {
      wasCleared: normalizedWasCleared,
      sortKey: normalizedSortKey,
      sortDirection: normalizedDirection
    }
  };

  localStorage.setItem(storageKey, JSON.stringify(state));

  return state;
}

export function clearSortPanelState(tableName: string): void {
  const storageKey = getSortPanelStorageKey(tableName);

  if (!storageKey) {
    return;
  }

  localStorage.removeItem(storageKey);
}

function isValidStoredSortPanelState(value: any): value is StoredSortPanelState {
  if (!value) {
    return false;
  }

  if (value.version !== 2) {
    return false;
  }

  const valuesByFieldKey = value.valuesByFieldKey;

  if (!valuesByFieldKey || typeof valuesByFieldKey !== 'object') {
    return false;
  }

  const wasCleared = valuesByFieldKey.wasCleared;
  const sortKey = valuesByFieldKey.sortKey;
  const sortDirection = valuesByFieldKey.sortDirection;

  const isValidWasCleared = typeof wasCleared === 'boolean';
  if (!isValidWasCleared) {
    return false;
  }

  // if cleared, allow missing key/direction
  if (wasCleared === true) {
    const keyIsNull = sortKey == null;
    const keyIsNonEmptyString = typeof sortKey === 'string' && sortKey.trim().length > 0;
    const isValidKeyWhenCleared = keyIsNull || keyIsNonEmptyString;

    const directionIsNull = sortDirection == null;
    const directionIsValid = sortDirection === 'ASC' || sortDirection === 'DESC';
    const isValidDirectionWhenCleared = directionIsNull || directionIsValid;

    if (!isValidKeyWhenCleared || !isValidDirectionWhenCleared) {
      return false;
    }

    return true;
  }

  // if not cleared, require valid key + direction
  const hasValidKey = typeof sortKey === 'string' && sortKey.trim().length > 0;
  const hasValidDirection = sortDirection === 'ASC' || sortDirection === 'DESC';

  if (!hasValidKey || !hasValidDirection) {
    return false;
  }

  return true;
}

function normalizeNullableString(value: any): string | null {
  const normalized = String(value ?? '').trim();

  if (normalized.length === 0) {
    return null;
  }

  return normalized;
}
